import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { formatDistanceToNow } from 'date-fns'
import { nl } from 'date-fns/locale'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Loader2,
  Calendar,
  User,
  Database,
  FileIcon,
} from 'lucide-react'
import { usePracticeCaseArticles } from '@/api/practice-cases'
import type { PracticeCase } from '@/api/practice-cases'

interface ArticleJson {
  title?: string
  summary?: string
  content?: string
  published_at?: string
  publishedAt?: string
  sourceType?: string
  sourceName?: string
  url?: string
  timestamp?: string
  [key: string]: unknown
}

interface ViewPracticeCaseDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  practiceCase: PracticeCase | null
}

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'ready':
      return (
        <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
          <CheckCircle className="h-3 w-3 mr-1" />
          Klaar
        </Badge>
      )
    case 'generating':
      return (
        <Badge variant="outline" className="bg-blue-100 text-blue-800 border-blue-300">
          <Loader2 className="h-3 w-3 mr-1 animate-spin" />
          Genereren...
        </Badge>
      )
    case 'queued':
      return (
        <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
          <Clock className="h-3 w-3 mr-1" />
          In wachtrij
        </Badge>
      )
    case 'error':
      return (
        <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
          <AlertCircle className="h-3 w-3 mr-1" />
          Fout
        </Badge>
      )
    default:
      return <Badge variant="outline">{status}</Badge>
  }
}

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

export function ViewPracticeCaseDialog({
  open,
  onOpenChange,
  practiceCase,
}: ViewPracticeCaseDialogProps) {
  const { data: articlesData, isLoading: articlesLoading } = usePracticeCaseArticles(
    practiceCase?.id || null
  )

  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-'
    return formatDistanceToNow(new Date(dateString), {
      addSuffix: true,
      locale: nl,
    })
  }

  const formatDateTime = (dateString: string | null): string => {
    if (!dateString) return '-'
    return new Date(dateString).toLocaleString('nl-NL')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Oefenscenario Details
          </DialogTitle>
        </DialogHeader>

        {practiceCase && (
          <div className="max-h-[75vh] overflow-y-auto pr-4">
            <div className="space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Basisinformatie</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-muted-foreground">Titel</Label>
                      <p className="font-medium">{practiceCase.title}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Locatie</Label>
                      <p className="font-medium">{practiceCase.location}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Status</Label>
                      <div className="mt-1">{getStatusBadge(practiceCase.status)}</div>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Artikelen</Label>
                      <p className="font-medium">{practiceCase.articleCount}</p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Gemaakt door</Label>
                      <p className="font-medium flex items-center gap-2">
                        <User className="h-4 w-4" />
                        {practiceCase.createdBy?.name || '-'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-muted-foreground">Gemaakt op</Label>
                      <p className="font-medium flex items-center gap-2">
                        <Calendar className="h-4 w-4" />
                        {formatDateTime(practiceCase.createdAt)}
                      </p>
                    </div>
                  </div>

                  {practiceCase.status === 'error' && practiceCase.errorMessage && (
                    <div className="bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        <strong>Fout:</strong> {practiceCase.errorMessage}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Generation Information */}
              {(practiceCase.generationStartedAt ||
                practiceCase.generationCompletedAt ||
                practiceCase.generationModel) && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Generatie Informatie</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {practiceCase.generationStartedAt && (
                        <div>
                          <Label className="text-muted-foreground">Gestart op</Label>
                          <p className="font-medium">
                            {formatDateTime(practiceCase.generationStartedAt)}
                          </p>
                        </div>
                      )}
                      {practiceCase.generationCompletedAt && (
                        <div>
                          <Label className="text-muted-foreground">Voltooid op</Label>
                          <p className="font-medium">
                            {formatDateTime(practiceCase.generationCompletedAt)}
                          </p>
                        </div>
                      )}
                      {practiceCase.generationModel && (
                        <div>
                          <Label className="text-muted-foreground">AI Model</Label>
                          <p className="font-medium">{practiceCase.generationModel}</p>
                        </div>
                      )}
                      {practiceCase.generationTokens && (
                        <div>
                          <Label className="text-muted-foreground">Tokens Gebruikt</Label>
                          <p className="font-medium">
                            {practiceCase.generationTokens.toLocaleString()}
                          </p>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* File Metadata */}
              {practiceCase.fileMetadata && practiceCase.fileMetadata.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Bronbestanden</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {practiceCase.fileMetadata.map((file, index) => (
                        <div key={index} className="flex items-center gap-3 p-3 border rounded-md">
                          <FileIcon className="h-5 w-5 text-muted-foreground" />
                          <div className="flex-1">
                            <p className="font-medium">{file.originalFilename}</p>
                            <p className="text-sm text-muted-foreground">
                              {file.fileType} • {formatFileSize(file.fileSize)} • Geüpload{' '}
                              {formatDate(file.uploadedAt)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Generated Articles */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Database className="h-5 w-5" />
                    Gegenereerde Artikelen ({articlesData?.data?.length || 0})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {articlesLoading ? (
                    <div className="flex justify-center items-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin" />
                      <span className="ml-2">Artikelen laden...</span>
                    </div>
                  ) : articlesData?.data?.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>Geen artikelen gevonden</p>
                      <p className="text-sm mt-2">
                        {practiceCase.status === 'ready'
                          ? 'Er zijn nog geen artikelen gegenereerd voor dit scenario.'
                          : 'Artikelen worden nog gegenereerd.'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {articlesData?.data?.map((article, index) => (
                        <div key={article.id}>
                          <div className="border rounded-lg p-4">
                            <div className="flex justify-between items-start mb-3">
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="text-xs">
                                  Artikel {index + 1}
                                </Badge>
                                {article.syntheticFlag && (
                                  <Badge variant="secondary" className="text-xs">
                                    Synthetisch
                                  </Badge>
                                )}
                              </div>
                              <div className="text-sm text-muted-foreground">
                                {formatDate(article.createdAt)}
                              </div>
                            </div>

                            {article.articleJson && (
                              <div className="space-y-3">
                                {(article.articleJson as ArticleJson).title && (
                                  <div>
                                    <Label className="text-sm font-medium">Titel</Label>
                                    <p className="text-sm mt-1">
                                      {String((article.articleJson as ArticleJson).title)}
                                    </p>
                                  </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                  {(article.articleJson as ArticleJson).sourceType && (
                                    <div>
                                      <Label className="text-sm font-medium">Bron Type</Label>
                                      <p className="text-sm mt-1">
                                        {String((article.articleJson as ArticleJson).sourceType)}
                                      </p>
                                    </div>
                                  )}

                                  {((article.articleJson as ArticleJson).publishedAt ||
                                    (article.articleJson as ArticleJson).published_at) && (
                                    <div>
                                      <Label className="text-sm font-medium">Publicatietijd</Label>
                                      <p className="text-sm mt-1">
                                        {new Date(
                                          String(
                                            (article.articleJson as ArticleJson).publishedAt ||
                                              (article.articleJson as ArticleJson).published_at
                                          )
                                        ).toLocaleString('nl-NL')}
                                      </p>
                                    </div>
                                  )}
                                </div>

                                {(article.articleJson as ArticleJson).summary && (
                                  <div>
                                    <Label className="text-sm font-medium">Samenvatting</Label>
                                    <p className="text-sm mt-1 text-muted-foreground">
                                      {String((article.articleJson as ArticleJson).summary)}
                                    </p>
                                  </div>
                                )}

                                {(article.articleJson as ArticleJson).content && (
                                  <div>
                                    <Label className="text-sm font-medium">Inhoud</Label>
                                    <div className="text-sm mt-1 max-h-32 overflow-y-auto bg-muted/50 p-3 rounded border">
                                      {String((article.articleJson as ArticleJson).content)}
                                    </div>
                                  </div>
                                )}

                                {(article.articleJson as ArticleJson).url && (
                                  <div>
                                    <Label className="text-sm font-medium">URL</Label>
                                    <p className="text-sm mt-1">
                                      <a
                                        href={String((article.articleJson as ArticleJson).url)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:text-blue-800 underline break-all"
                                      >
                                        {String((article.articleJson as ArticleJson).url)}
                                      </a>
                                    </p>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          {index < (articlesData.data?.length || 0) - 1 && (
                            <hr className="my-4 border-muted" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Sluiten
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
